import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowDown,
  ArrowUpRight,
  Asterisk,
  BookOpen,
  Check,
  Landmark,
  Plus,
  ScrollText,
  Star,
  TrendingUp,
} from "lucide-react";
import { EASE } from "@/components/Reveal";
import { GOOGLE_PROFILE_URL, GOOGLE_REVIEW_URL } from "@/components/Navbar";

const SKYLINE_IMG =
  "https://images.unsplash.com/photo-1544214036-5aaeb9e32d11?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxzaW5nYXBvcmUlMjBza3lsaW5lJTIwbmlnaHQlMjB3aW5kb3d8ZW58MHx8fHwxNzkyMjg1NDcwfDA&ixlib=rb-4.1.0&q=85&auto=format&fit=crop&w=1200";

const SERVICES = [
  {
    id: "accounting",
    title: "Accounting & Bookkeeping Services",
    theme: "dark",
    icon: BookOpen,
    desc: "Day-to-day bookkeeping, financial statements and IRAS-ready reports that keep your numbers accurate and organised.",
    features: ["Monthly bookkeeping", "Financial statements"],
  },
  {
    id: "secretarial",
    title: "Corporate Secretarial",
    theme: "sage",
    icon: ScrollText,
    desc: "Incorporation, statutory filings and ACRA compliance handled carefully, so your company stays in good standing.",
    features: ["Company incorporation", "Annual filings"],
  },
  {
    id: "grants",
    title: "Business Grants",
    theme: "mint",
    icon: TrendingUp,
    desc: "Identify and apply for the right government grants, with practical guidance at every step of the application.",
    features: ["Grant eligibility", "Application support"],
  },
  {
    id: "loans",
    title: "SME Business Loans",
    theme: "photo",
    icon: Landmark,
    desc: "Explore financing options for your SME with practical guidance on loan requirements and application preparation.",
    features: ["Financing options", "Application guidance"],
  },
];

const THEMES = {
  dark: "bg-forest text-ivory",
  sage: "bg-sage text-charcoal",
  mint: "bg-mint text-charcoal",
  photo: "bg-forest text-ivory",
};

const FadeUp = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

const ServiceBar = ({ s, active, onActivate, onEnquire }) => (
  <div
    role="button"
    tabIndex={0}
    aria-expanded={active}
    onMouseEnter={onActivate}
    onClick={onActivate}
    onKeyDown={(e) => e.key === "Enter" && onActivate()}
    data-testid={`service-bar-${s.id}`}
    className={`relative cursor-pointer overflow-hidden rounded-xl px-6 py-5 outline-none md:px-8 md:py-6 lg:flex lg:h-full lg:flex-col lg:justify-center ${THEMES[s.theme]}`}
  >
    {s.theme === "photo" && (
      <>
        <img
          src={SKYLINE_IMG}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-forest/70" aria-hidden="true" />
      </>
    )}
    <s.icon
      className="pointer-events-none absolute right-20 top-4 h-16 w-16 opacity-[0.07]"
      strokeWidth={1}
      aria-hidden="true"
    />
    <div className="relative flex items-center justify-between gap-4">
      <h3 className="font-body text-lg font-semibold tracking-tight md:text-2xl">
        {s.title}
      </h3>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-current opacity-70">
        <Plus
          className={`h-4 w-4 transition-transform duration-300 ${
            active ? "rotate-45" : ""
          }`}
        />
      </span>
    </div>
    <AnimatePresence initial={false}>
      {active && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.45, ease: EASE }}
          className="relative overflow-hidden"
        >
          <p className="mt-4 max-w-md text-sm leading-relaxed opacity-80">
            {s.desc}
          </p>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1.5 text-[13px] font-medium">
            {s.features.map((f) => (
              <span key={f} className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5" />
                {f}
              </span>
            ))}
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEnquire();
            }}
            data-testid={`service-enquire-${s.id}`}
            className="group mt-4 inline-flex items-center gap-1.5 text-sm font-semibold underline decoration-current/40 underline-offset-4 transition-colors hover:decoration-current"
          >
            Enquire about this service
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const Stars = () => (
  <div className="flex gap-1 text-[#FBBC04]" aria-label="Rated 5 out of 5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className="h-3.5 w-3.5" fill="currentColor" strokeWidth={0} />
    ))}
  </div>
);

const ReviewCard = ({ title, sub, footer, filled }) => (
  <a
    href={GOOGLE_PROFILE_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={filled ? "google-rating-card" : "google-review-card"}
    className={`block rounded-xl p-4 transition-shadow duration-300 hover:shadow-md ${
      filled ? "bg-sage" : "border border-charcoal/10 bg-white/60"
    }`}
  >
    <Stars />
    <p className="mt-2.5 text-sm font-semibold text-charcoal">{title}</p>
    <p className="mt-0.5 text-xs text-charcoal/55">{sub}</p>
    <p className="mt-3 flex items-center gap-1.5 border-t border-charcoal/10 pt-2.5 text-[11px] text-charcoal/50">
      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-white text-[10px] font-bold text-[#4285F4] shadow-sm">
        G
      </span>
      {footer}
    </p>
  </a>
);

const Home = ({ onEnquire }) => {
  const [active, setActive] = useState("loans");

  return (
    <main className="flex min-h-0 flex-1 flex-col">
      <div className="mx-auto grid w-full max-w-[1700px] flex-1 gap-12 px-5 pb-6 pt-8 md:px-10 lg:grid-cols-[50%_50%] lg:gap-14 lg:pt-6">
        <div className="flex flex-col">
          <FadeUp delay={0.05} className="mb-9 lg:mb-8">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-charcoal/50">
                Customer Experiences
              </span>
              <a
                href={GOOGLE_REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="google-review-link"
                className="group inline-flex items-center gap-5 border-b border-charcoal pb-1.5 text-[13px] font-semibold text-charcoal"
              >
                Leave a Google Review
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <ReviewCard
                title="Google Reviews"
                sub="Rated 5.0 · 1 review"
                footer="Google · Business profile"
                filled
              />
              <ReviewCard
                title={"\u201CExcellent and reliable service. They pay great attention to detail.\u201D"}
                sub="SianLe Tey · Google review"
                footer="Google · 2 months ago"
              />
            </div>
          </FadeUp>

          <FadeUp delay={0.12}>
            <span className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-charcoal/55">
              <span className="h-px w-7 bg-charcoal/40" aria-hidden="true" />
              Your Business. Our Expertise.
            </span>
          </FadeUp>

          <FadeUp delay={0.12}>
            <h1
              data-testid="hero-heading"
              className="mt-5 font-body text-[40px] font-semibold leading-[1.03] tracking-[-0.035em] text-charcoal sm:text-5xl lg:text-[56px] xl:text-[64px]"
            >
              What does your
              <br className="hidden lg:block" /> business need{" "}
              <span className="font-serif font-normal italic text-[#7D8F84]">
                today?
              </span>
            </h1>
          </FadeUp>

          <FadeUp delay={0.2} className="mt-5">
            <p className="text-[15px] font-medium text-charcoal">
              Four services. One practical partner.
            </p>
            <p className="mt-1 text-[15px] text-charcoal/55">
              Support for every stage of your Singapore business.
            </p>
          </FadeUp>

          <FadeUp delay={0.28} className="mt-9 lg:mt-8">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-charcoal/50">
                01 — About Cosmic
              </span>
              <Asterisk className="h-4 w-4 text-charcoal/35" aria-hidden="true" />
            </div>
            <h2 className="mt-3 font-body text-[26px] font-semibold leading-[1.15] tracking-[-0.02em] text-charcoal md:text-[28px]">
              More clarity.
              <br />
              Room to move forward.
            </h2>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-charcoal/60">
              Whether you need end-to-end administration or targeted expertise,
              we&apos;re here to support your journey. We handle the details, so
              you can focus on your business.
            </p>
            <button
              type="button"
              onClick={onEnquire}
              data-testid="meet-partner-link"
              className="group mt-5 inline-flex items-center gap-8 border-b border-charcoal pb-2 text-sm font-semibold text-charcoal"
            >
              Meet your business partner
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <p className="mt-4 text-xs text-charcoal/50">
              Singapore-based&nbsp;&nbsp;·&nbsp;&nbsp;Built for
              SMEs&nbsp;&nbsp;·&nbsp;&nbsp;IRAS &amp; ACRA
            </p>
          </FadeUp>
        </div>

        <div className="flex flex-col">
          <FadeUp
            delay={0.15}
            className="flex items-center justify-between gap-4"
          >
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-charcoal/50">
              How We Can Help
            </span>
            <span className="flex items-center gap-1.5 text-xs text-charcoal/50">
              Hover to explore
              <ArrowDown className="h-3.5 w-3.5" />
            </span>
          </FadeUp>

          <div className="mt-4 flex flex-col gap-2.5 lg:flex-1">
            {SERVICES.map((s, i) => (
              <FadeUp key={s.id} delay={0.2 + i * 0.07} className="lg:flex lg:flex-1 lg:flex-col">
                <ServiceBar
                  s={s}
                  active={active === s.id}
                  onActivate={() => setActive(s.id)}
                  onEnquire={onEnquire}
                />
              </FadeUp>
            ))}
          </div>

          <FadeUp
            delay={0.5}
            className="mt-5 flex flex-wrap items-center justify-between gap-3"
          >
            <p className="text-[13px] text-charcoal/50">
              Not sure where to start?
            </p>
            <button
              type="button"
              onClick={onEnquire}
              data-testid="next-step-link"
              className="group inline-flex items-center gap-5 border-b border-charcoal pb-1.5 text-sm font-semibold text-charcoal"
            >
              Let&apos;s find your next step
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </FadeUp>
        </div>
      </div>

      <footer data-testid="footer" className="border-t border-charcoal/10">
        <div className="mx-auto flex w-full max-w-[1700px] flex-col items-center gap-1.5 px-5 py-3 text-[11px] text-charcoal/45 md:flex-row md:justify-between md:px-10">
          <p>© 2026 Cosmic Division Pte. Ltd. · UEN 202400141Z</p>
          <div className="flex flex-col items-center gap-1.5 md:flex-row md:gap-6">
            <a
              href="tel:+6592366649"
              data-testid="footer-phone-link"
              className="transition-colors duration-300 hover:text-charcoal"
            >
              +65 9236 6649
            </a>
            <a
              href="mailto:pedro.lim@cosmic-div.com"
              data-testid="footer-email-link"
              className="transition-colors duration-300 hover:text-charcoal"
            >
              pedro.lim@cosmic-div.com
            </a>
            <a
              href="https://www.carousell.sg/p/professional-accounting-bookkeeping-services-gst-payroll-tax-company-loyal-support-1401991798/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-carousell-link"
              className="transition-colors duration-300 hover:text-charcoal"
            >
              Carousell
            </a>
            <p>8A Admiralty St, FoodXchange #02-26, Singapore 757437</p>
          </div>
        </div>
      </footer>
    </main>
  );
};

export default Home;
